import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';

import CommonSmartAlertIconComponent from 'webapp/components/common/smart-alert-icon';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';
import ScrollableTextComponent from 'webapp/components/scrollable-text';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Annotation from 'webapp/models-custom/annotation';

export default class AnnotationPopupListComponent extends PopupInnerContextComponent<PopupData.Annotation.List> {
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked isFixed = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// triggered from d-chart/annotations/base
		this.args.model.on('checkIsFixed', this, () => {
			return this.isFixed;
		});

		this.args.model.on('toggleIsFixed', this, () => {
			this.isFixed = !this.isFixed;
		});
	}

	get model() {
		return this.popupData.model;
	}

	get showActions(): boolean {
		return this.popupData.canEdit && this.isFixed;
	}

	// the native app deletes nothing
	get showAnomalyDelete(): boolean {
		return this.isFixed && !this.navigationService.isMobileApp;
	}

	@action
	getMetricName(model: Annotation) {
		const metricSetupData = model.metricSetup.metricSetupData;
		let metricName = '';

		if (metricSetupData) {
			const dataSelect = this.popupData.getDataselectByMetricSetupData(metricSetupData);
			metricName = dataSelect?.dataRoot?.metricName ?? '';
		}

		const attributeName = model.metricSetup.firstAttributeNameFromBasicFilters,
			unitName = model.metricSetup.unitName;

		if (attributeName) {
			metricName += ` / ${attributeName}`;
		}

		if (unitName) {
			metricName += ` / ${unitName}`;
		}

		return metricName;
	}

	@action
	getAnomalyScoreStyles(model: Annotation) {
		const extra = model.extra;
		const variant =
			extra && model.metricSetup.metricSetupData
				? this.popupData.getAnomalyScoreColors(
						model.metricSetup.metricSetupData,
						extra.score,
						extra.lowerValue,
						extra.upperValue,
					)
				: null;

		return variant
			? `color: var(--${variant.color}); background-color: var(--${variant.bgColor});`
			: '';
	}

	@action
	create(): void {
		this.popupData.create();
	}

	@action
	edit(model: Annotation): void {
		this.popupData.edit(model);
	}

	@action
	delete(model: Annotation): void {
		model
			.delete()
			.then(() => {
				this.popupData.delete(model);
				this.close();
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	deleteWithConfirm(model: Annotation): void {
		const metricName = this.getMetricName(model);

		this.popupService.openConfirmPopup({
			title: `Do you want to delete this anomaly annotation for "${metricName}"?`,
			text: "Deleting this annotation will permanently remove the saved anomaly. You won't be able to view or restore it on the chart.",
			onConfirm: () => this.delete(model),
		});
	}
	<template>
		<div class="content {{if this.showActions 'show-actions'}}">
			<ul class="list">
				{{#each this.model as |model|}}
					{{#if model.isAnomalyType}}
						<li class="item item-anomaly">
							<div class="item-anomaly__title">
								<div class="metric-name">
									<ScrollableTextComponent>
										{{this.getMetricName model}}
									</ScrollableTextComponent>
								</div>
								{{#if this.showAnomalyDelete}}
									<div class="actions">
										<a
											role="button"
											class="delete"
											{{on "click" (fn this.deleteWithConfirm model)}}
										>
											<CommonUiIconComponent @name="Trash" @size="sm" />
										</a>
									</div>
								{{/if}}
							</div>
							<div class="item-anomaly__header">
								<CommonSmartAlertIconComponent />
								<h6>Anomaly detected</h6>
								<span style={{this.getAnomalyScoreStyles model}}>
									{{model.extra.score}}
								</span>
							</div>
							<div class="item-anomaly__body">
								<h6>Expected range</h6>
								<div>
									Highest value:
									<strong>{{model.extra.upperValue}}</strong>
								</div>
								<div>
									Lowest value:
									<strong>{{model.extra.lowerValue}}</strong>
								</div>
							</div>
						</li>
					{{else}}
						<li class="item" data-id={{model.id}}>
							<div class="item__content">
								{{{model.noteFormatted}}}

								<small>
									<CommonUiIconComponent @name="AnnotationEmptyFill" @size="xs" />
									{{#if model.displayTime}}{{model.displayTime}}, {{/if}}noted by
									{{model.displayName}}
								</small>

								{{#if this.showActions}}
									<div class="actions">
										<a
											role="button"
											class="edit"
											{{on "click" (fn this.edit model)}}
										>
											<CommonUiIconComponent @name="Pencil" @size="sm" />
										</a>
										<a
											role="button"
											class="delete"
											{{on "click" (fn this.delete model)}}
										>
											<CommonUiIconComponent @name="Trash" @size="sm" />
										</a>
									</div>
								{{/if}}
							</div>
						</li>
					{{/if}}
				{{/each}}
			</ul>

			{{#if this.showActions}}
				<SubmitButtonComponent
					@text="New annotation"
					@extraClass="add"
					@size="small"
					@onTrigger={{this.create}}
				/>
			{{/if}}
		</div>
	</template>
}
